import { Head, Link } from "@inertiajs/react"
import ProductionHeader from "@/components/admin/production/ProductionHeader"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { SharedProductionProps } from "@/components/admin/production/types"

interface BatchDetail {
  id: number
  label: string
  produced_on: string
  notes: string | null
  total_units: number
  direct_cost_cents: number
  variants: Array<{
    id: number
    item_variant_id: number
    item_name: string
    label: string
    units_produced: number
  }>
  cost_lines: Array<{
    id: number
    kind: string
    label: string
    amount_cents: number
    supplier_name: string | null
  }>
  asset_usages: Array<{
    id: number
    production_asset_id: number
    label: string
  }>
}

interface ShowProps extends SharedProductionProps {
  batch: BatchDetail
}

function formatEuros(cents: number): string {
  return (cents / 100).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  })
}

export default function BatchShow({ store, paths, batch }: ShowProps) {
  return (
    <>
      <Head title={`Batch ${batch.label}`} />
      <ProductionHeader
        title={batch.label}
        subtitle={`Produit le ${batch.produced_on}`}
        storeName={store.name ?? undefined}
        paths={paths}
        actions={
          <Link href={`${paths.batches_index}/${batch.id}/edit`}>
            <Button variant="outline" size="sm">
              Éditer
            </Button>
          </Link>
        }
      />
      <main className="mx-auto max-w-4xl px-6 py-8 space-y-8">
        <section className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase text-gray-500">Unités totales</p>
            <p className="mt-1 text-2xl font-semibold">{batch.total_units}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase text-gray-500">Coût direct</p>
            <p className="mt-1 text-2xl font-semibold">
              {formatEuros(batch.direct_cost_cents)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase text-gray-500">Équipements liés</p>
            <p className="mt-1 text-2xl font-semibold">
              {batch.asset_usages.length}
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Variantes produites</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Article</TableHead>
                <TableHead>Variante</TableHead>
                <TableHead>Unités</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batch.variants.map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{v.item_name}</TableCell>
                  <TableCell>{v.label}</TableCell>
                  <TableCell>{v.units_produced}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Lignes de coût</h2>
          {batch.cost_lines.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune ligne enregistrée.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Fournisseur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batch.cost_lines.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{l.kind}</TableCell>
                    <TableCell>{l.label}</TableCell>
                    <TableCell>{formatEuros(l.amount_cents)}</TableCell>
                    <TableCell>{l.supplier_name ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Équipements liés</h2>
          {batch.asset_usages.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun équipement.</p>
          ) : (
            <ul className="list-disc pl-5 text-sm">
              {batch.asset_usages.map((u) => (
                <li key={u.id}>{u.label}</li>
              ))}
            </ul>
          )}
        </section>

        {batch.notes && (
          <section>
            <h2 className="text-lg font-semibold mb-2">Notes</h2>
            <p className="text-sm whitespace-pre-wrap">{batch.notes}</p>
          </section>
        )}
      </main>
    </>
  )
}
